import { useCallback, useEffect, useState } from "react";
import { api, API } from "../lib/api";
import { useSiteImages } from "../context/SiteImagesContext";
import { toast } from "sonner";
import {
  LockKey, UploadSimple, Trash, CheckCircle, SpinnerGap,
  Package, Images, ArrowClockwise, MapPin, Truck, EnvelopeSimple, ShoppingCart,
} from "@phosphor-icons/react";

const KEY_STORE = "vitae_admin_key";
const FULFILLMENT = ["nuevo", "preparando", "enviado"];
const FULFILLMENT_STYLE = {
  nuevo: "bg-[#F2C94C]/20 text-[#8a6d1a]",
  preparando: "bg-[#6BA6D9]/20 text-[#2f5f8a]",
  enviado: "bg-[#A3B18A]/25 text-[#4f6242]",
};
const pct = (part, whole) => (whole > 0 ? Math.round((part / whole) * 100) : 0);

export default function Admin() {
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem(KEY_STORE) || "");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(false);
  const [tab, setTab] = useState("orders");
  const [slots, setSlots] = useState([]);
  const [uploaded, setUploaded] = useState([]);
  const [busy, setBusy] = useState("");
  const { refresh } = useSiteImages();

  // Orders state
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [onlyPaid, setOnlyPaid] = useState(true);
  const [modelNames, setModelNames] = useState({});
  const [finishLabels, setFinishLabels] = useState({});
  const [penLabels, setPenLabels] = useState({});
  const [magnetLabels, setMagnetLabels] = useState({});
  const [videoUrl, setVideoUrl] = useState("");
  const [savingVideo, setSavingVideo] = useState(false);
  const [stats, setStats] = useState(null);
  const [carts, setCarts] = useState([]);

  const loadSlots = async () => {
    const { data } = await api.get("/image-slots");
    setSlots(data.slots || []);
    setUploaded(data.uploaded || []);
    try {
      const { data: pub } = await api.get("/public-settings");
      setVideoUrl(pub.home_video_url || "");
    } catch { /* sin vídeo configurado */ }
  };

  const saveVideo = async () => {
    setSavingVideo(true);
    try {
      await api.put("/admin/settings/home_video_url", { value: videoUrl },
        { headers: { "x-admin-key": adminKey } });
      toast.success("Vídeo guardado — ya se ve en la web");
    } catch {
      toast.error("No se pudo guardar el vídeo");
    } finally {
      setSavingVideo(false);
    }
  };

  const loadOrders = useCallback(async (key) => {
    setOrdersLoading(true);
    try {
      const [{ data: od }, { data: cols }, { data: pricing }, { data: st }, { data: ac }] = await Promise.all([
        api.get("/admin/orders", { headers: { "x-admin-key": key } }),
        api.get("/collections"),
        api.get("/pricing"),
        api.get("/admin/stats", { headers: { "x-admin-key": key } }),
        api.get("/admin/abandoned-carts", { headers: { "x-admin-key": key } }),
      ]);
      setOrders(od.orders || []);
      setStats(st || null);
      setCarts(ac.carts || []);
      setModelNames(Object.fromEntries((cols || []).map((c) => [c.id, c.name])));
      setFinishLabels(Object.fromEntries((pricing.finishes || []).map((f) => [f.id, f.label])));
      setPenLabels(Object.fromEntries((pricing.pens?.colors || []).map((c) => [c.id, c.label])));
      setMagnetLabels(Object.fromEntries((pricing.magnets?.designs || []).map((d) => [d.id, d.label])));
    } catch {
      toast.error("No se pudieron cargar los pedidos");
    } finally {
      setOrdersLoading(false);
    }
  }, []);

  const verify = async (key) => {
    setChecking(true);
    try {
      await api.post("/admin/verify", {}, { headers: { "x-admin-key": key } });
      sessionStorage.setItem(KEY_STORE, key);
      setAuthed(true);
      await Promise.all([loadSlots(), loadOrders(key)]);
    } catch {
      toast.error("Clave incorrecta");
      setAuthed(false);
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    if (adminKey) verify(adminKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFulfillment = async (sessionId, value) => {
    try {
      await api.patch(`/admin/orders/${sessionId}`, { fulfillment: value },
        { headers: { "x-admin-key": adminKey } });
      setOrders((prev) => prev.map((o) => o.session_id === sessionId ? { ...o, fulfillment: value } : o));
      toast.success(`Pedido marcado como "${value}"`);
    } catch {
      toast.error("No se pudo actualizar el pedido");
    }
  };

  const handleFile = (slotKey, file) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error("La imagen supera 6 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result;
      const base64 = String(dataUrl).split(",")[1];
      setBusy(slotKey);
      try {
        await api.post(
          "/admin/images",
          { key: slotKey, content_type: file.type, data: base64 },
          { headers: { "x-admin-key": adminKey } }
        );
        toast.success("Imagen actualizada");
        await loadSlots();
        await refresh();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "No se pudo subir la imagen");
      } finally {
        setBusy("");
      }
    };
    reader.readAsDataURL(file);
  };

  const removeImage = async (slotKey) => {
    setBusy(slotKey);
    try {
      await api.delete(`/admin/images/${slotKey}`, { headers: { "x-admin-key": adminKey } });
      toast.success("Imagen eliminada");
      await loadSlots();
      await refresh();
    } catch {
      toast.error("No se pudo eliminar");
    } finally {
      setBusy("");
    }
  };

  const describeItem = (it) => {
    if (it.kind === "infantil") {
      return `Carpeta médica infantil VITAE × ${it.quantity}`;
    }
    if (it.kind === "magnet") {
      if (it.magnet_design === "personalizado") {
        return `Imán personalizado VITAE × ${it.quantity} · con ${(it.upload_ids || []).length} imagen(es) del cliente`;
      }
      return `Imán solidario · ${magnetLabels[it.magnet_design] || it.magnet_design || ""} × ${it.quantity}`;
    }
    if (it.kind === "pen") {
      return `Bolígrafo VITAE · ${penLabels[it.pen_color] || it.pen_color} × ${it.quantity}`;
    }
    if (it.kind === "specialty_pack") {
      return `Pack de 5 especialidades: ${(it.specialties || []).join(", ")} (×${it.quantity})`;
    }
    const parts = [
      `${modelNames[it.model_id] || it.model_id} · ${it.size} fundas · ${finishLabels[it.finish] || it.finish || ""}`,
    ];
    if (it.specialties?.length) parts.push(`Especialidades: ${it.specialties.join(", ")}`);
    if (it.name_personalization) parts.push(`Nombre: ${it.name_personalization}`);
    if (it.gift_wrap) parts.push(`Regalo${it.gift_message ? ` · “${it.gift_message}”` : ""}`);
    if (it.quantity > 1) parts.push(`Cantidad: ${it.quantity}`);
    return parts.join("  ·  ");
  };

  const visibleOrders = onlyPaid ? orders.filter((o) => o.payment_status === "paid") : orders;

  if (!authed) {
    return (
      <main data-testid="admin-login" className="min-h-screen flex items-center justify-center bg-[#FAF9F6] px-6">
        <div className="w-full max-w-sm bg-white border border-[#EBEBEB] rounded-3xl p-10 text-center">
          <LockKey size={40} weight="light" className="text-[#8A9A6F] mx-auto" />
          <h1 className="font-display text-3xl text-[#2D3732] mt-4">Panel de VITAE</h1>
          <p className="text-sm text-[#6B705C] mt-2 mb-6">Introduce tu clave para gestionar la tienda.</p>
          <input
            data-testid="admin-key-input"
            type="password"
            value={adminKey}
            onChange={(e) => setAdminKey(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && verify(adminKey)}
            placeholder="Clave de administrador"
            className="w-full bg-[#FAF9F6] border border-[#EBEBEB] rounded-2xl px-5 py-3 text-[#2D3732] focus:outline-none focus:ring-2 focus:ring-[#A3B18A]"
          />
          <button
            data-testid="admin-login-btn"
            onClick={() => verify(adminKey)}
            disabled={checking || !adminKey}
            className="w-full mt-4 bg-[#A3B18A] hover:bg-[#8A9A6F] text-white rounded-full py-3 text-sm transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {checking ? <SpinnerGap size={18} className="animate-spin" /> : "Entrar"}
          </button>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="admin-panel" className="min-h-screen bg-[#FAF9F6] pt-28 pb-24 px-6">
      <div className="max-w-4xl mx-auto">
        <p className="overline mb-3">Panel privado</p>
        <div className="flex items-center gap-2 mt-2 mb-8">
          <button
            data-testid="admin-tab-orders"
            onClick={() => setTab("orders")}
            className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm transition-colors ${
              tab === "orders" ? "bg-[#A3B18A] text-white" : "bg-white border border-[#EBEBEB] text-[#2D3732] hover:bg-[#EEF1E8]"
            }`}
          >
            <Package size={16} weight="light" /> Pedidos
          </button>
          <button
            data-testid="admin-tab-carts"
            onClick={() => setTab("carts")}
            className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm transition-colors ${
              tab === "carts" ? "bg-[#A3B18A] text-white" : "bg-white border border-[#EBEBEB] text-[#2D3732] hover:bg-[#EEF1E8]"
            }`}
          >
            <ShoppingCart size={16} weight="light" /> Carritos
          </button>
          <button
            data-testid="admin-tab-images"
            onClick={() => setTab("images")}
            className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm transition-colors ${
              tab === "images" ? "bg-[#A3B18A] text-white" : "bg-white border border-[#EBEBEB] text-[#2D3732] hover:bg-[#EEF1E8]"
            }`}
          >
            <Images size={16} weight="light" /> Imágenes
          </button>
        </div>

        {tab === "orders" && (
          <div data-testid="admin-orders-view">
            {stats && (
              <div data-testid="admin-stats" className="mb-8">
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-white border border-[#EBEBEB] rounded-2xl p-5 text-center">
                    <p data-testid="stats-today" className="font-display text-3xl text-[#2D3732]">{stats.today}</p>
                    <p className="text-xs text-[#9CA3AF] mt-1">Visitas hoy</p>
                  </div>
                  <div className="bg-white border border-[#EBEBEB] rounded-2xl p-5 text-center">
                    <p data-testid="stats-yesterday" className="font-display text-3xl text-[#2D3732]">{stats.days[1]?.visits ?? 0}</p>
                    <p className="text-xs text-[#9CA3AF] mt-1">Ayer</p>
                  </div>
                  <div className="bg-white border border-[#EBEBEB] rounded-2xl p-5 text-center">
                    <p data-testid="stats-total" className="font-display text-3xl text-[#2D3732]">{stats.total_30d}</p>
                    <p className="text-xs text-[#9CA3AF] mt-1">Últimos 30 días</p>
                  </div>
                  <div className="bg-white border border-[#EBEBEB] rounded-2xl p-5 text-center">
                    <p data-testid="stats-carts-today" className="font-display text-3xl text-[#2D3732]">{stats.carts_today ?? 0}</p>
                    <p className="text-xs text-[#9CA3AF] mt-1">Carritos hoy</p>
                  </div>
                  <div className="bg-white border border-[#EBEBEB] rounded-2xl p-5 text-center">
                    <p data-testid="stats-carts-yesterday" className="font-display text-3xl text-[#2D3732]">{stats.days[1]?.carts ?? 0}</p>
                    <p className="text-xs text-[#9CA3AF] mt-1">Carritos ayer</p>
                  </div>
                  <div className="bg-white border border-[#EBEBEB] rounded-2xl p-5 text-center">
                    <p data-testid="stats-carts-total" className="font-display text-3xl text-[#2D3732]">{stats.carts_30d ?? 0}</p>
                    <p className="text-xs text-[#9CA3AF] mt-1">Carritos 30 días</p>
                  </div>
                </div>
                <div data-testid="stats-funnel" className="bg-white border border-[#EBEBEB] rounded-2xl p-5 mt-3">
                  <p className="text-xs text-[#9CA3AF] mb-4">Embudo de conversión · últimos 30 días</p>
                  <div className="grid grid-cols-3 gap-2 sm:gap-3">
                    <div className="text-center">
                      <p data-testid="funnel-visits" className="font-display text-3xl text-[#2D3732]">{stats.total_30d}</p>
                      <p className="text-xs text-[#9CA3AF] mt-1">Visitas</p>
                    </div>
                    <div className="text-center">
                      <p data-testid="funnel-carts" className="font-display text-3xl text-[#2D3732]">{stats.carts_30d ?? 0}</p>
                      <p className="text-xs text-[#9CA3AF] mt-1">Añaden al carrito</p>
                      <p data-testid="funnel-carts-pct" className="text-xs text-[#8A9A6F] mt-0.5">{pct(stats.carts_30d ?? 0, stats.total_30d)}% de las visitas</p>
                    </div>
                    <div className="text-center">
                      <p data-testid="funnel-sales" className="font-display text-3xl text-[#2D3732]">{stats.sales_30d ?? 0}</p>
                      <p className="text-xs text-[#9CA3AF] mt-1">Compran</p>
                      <p data-testid="funnel-sales-pct" className="text-xs text-[#8A9A6F] mt-0.5">{pct(stats.sales_30d ?? 0, stats.carts_30d ?? 0)}% de los carritos</p>
                    </div>
                  </div>
                  <p className="text-xs text-[#9CA3AF] mt-4 text-center">
                    Conversión total: <span data-testid="funnel-total-pct" className="text-[#2D3732]">{pct(stats.sales_30d ?? 0, stats.total_30d)}%</span> de las visitas acaban en compra
                  </p>
                </div>
                {stats.days.length > 0 && (
                  <div className="bg-white border border-[#EBEBEB] rounded-2xl p-5 mt-3">
                    <p className="text-xs text-[#9CA3AF] mb-3">Visitas por día</p>
                    <div className="flex items-end gap-1.5 h-20">
                      {[...stats.days].slice(0, 14).reverse().map((d) => {
                        const max = Math.max(...stats.days.map((x) => x.visits), 1);
                        return (
                          <div key={d.date} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${d.visits}`}>
                            <span className="text-[0.6rem] text-[#6B705C]">{d.visits}</span>
                            <div
                              className="w-full rounded-t bg-[#A3B18A]"
                              style={{ height: `${Math.max(4, (d.visits / max) * 48)}px` }}
                            />
                            <span className="text-[0.55rem] text-[#9CA3AF]">{d.date.slice(8)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h1 className="font-display text-4xl text-[#2D3732]">Pedidos</h1>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm text-[#4A554E] cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="orders-filter-paid"
                    checked={onlyPaid}
                    onChange={(e) => setOnlyPaid(e.target.checked)}
                    className="accent-[#8A9A6F] w-4 h-4"
                  />
                  Solo pagados
                </label>
                <button
                  data-testid="orders-refresh"
                  onClick={() => loadOrders(adminKey)}
                  className="flex items-center gap-2 border border-[#A3B18A] text-[#2D3732] hover:bg-[#EEF1E8] rounded-full px-4 py-2 text-sm transition-colors"
                >
                  <ArrowClockwise size={15} className={ordersLoading ? "animate-spin" : ""} /> Actualizar
                </button>
              </div>
            </div>

            <div className="mt-8 space-y-4">
              {visibleOrders.length === 0 && !ordersLoading && (
                <p data-testid="orders-empty" className="text-[#6B705C] font-light bg-white border border-[#EBEBEB] rounded-2xl p-8 text-center">
                  No hay pedidos {onlyPaid ? "pagados " : ""}todavía. En cuanto entre una venta, aparecerá aquí con todos sus detalles.
                </p>
              )}
              {visibleOrders.map((o, i) => {
                const paid = o.payment_status === "paid";
                const fulfill = o.fulfillment || "nuevo";
                return (
                  <div
                    key={o.session_id}
                    data-testid={`order-card-${i}`}
                    className="bg-white border border-[#EBEBEB] rounded-2xl p-6"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="text-[#2D3732] font-medium">
                          {o.buyer_name || "Cliente"} {o.buyer_email ? `· ${o.buyer_email}` : ""}
                        </p>
                        <p className="text-xs text-[#9CA3AF] mt-1">
                          {o.created_at ? new Date(o.created_at).toLocaleString("es-ES") : ""} · {o.session_id.slice(0, 18)}…
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          data-testid={`order-status-${i}`}
                          className={`text-xs rounded-full px-3 py-1.5 ${paid ? "bg-[#A3B18A]/25 text-[#4f6242]" : "bg-[#F2C94C]/20 text-[#8a6d1a]"}`}
                        >
                          {paid ? "Pagado" : "Pendiente"}
                        </span>
                        <select
                          data-testid={`fulfillment-select-${i}`}
                          value={fulfill}
                          onChange={(e) => setFulfillment(o.session_id, e.target.value)}
                          className={`text-xs rounded-full px-3 py-1.5 border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#A3B18A] ${FULFILLMENT_STYLE[fulfill]}`}
                        >
                          {FULFILLMENT.map((f) => (
                            <option key={f} value={f}>{f.charAt(0).toUpperCase() + f.slice(1)}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <ul className="mt-4 space-y-1.5">
                      {(o.items || []).map((it, j) => (
                        <li key={j} data-testid={`order-item-${i}-${j}`} className="text-sm text-[#4A554E] font-light flex items-start gap-2 flex-wrap">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#A3B18A] mt-1.5 flex-shrink-0" />
                          {describeItem(it)}
                          {it.kind === "magnet" && (it.upload_ids || []).length > 0 && (
                            <span className="flex gap-2 flex-wrap">
                              {it.upload_ids.map((uid, k) => (
                                <a
                                  key={uid}
                                  data-testid={`order-upload-${i}-${j}-${k}`}
                                  href={`${API}/uploads/${uid}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[#8A9A6F] underline underline-offset-2"
                                >
                                  foto {k + 1}
                                </a>
                              ))}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>

                    <div className="mt-4 pt-4 border-t border-[#F0EEE7] flex flex-wrap gap-x-6 gap-y-2 text-xs text-[#6B705C]">
                      <span data-testid={`order-total-${i}`} className="text-sm text-[#2D3732] font-medium">
                        Productos: {((o.amount || 0) / 100).toFixed(2)} € <span className="text-[#9CA3AF] font-normal">+ envío</span>
                      </span>
                      {o.shipping_method && (
                        <span className="flex items-center gap-1.5"><Truck size={14} weight="light" /> {o.shipping_method}</span>
                      )}
                      {o.shipping_address && (
                        <span data-testid={`order-address-${i}`} className="flex items-center gap-1.5"><MapPin size={14} weight="light" /> {o.shipping_address}</span>
                      )}
                      <span className="flex items-center gap-1.5">
                        <EnvelopeSimple size={14} weight="light" />
                        Email cliente: {o.buyer_email_sent ? "enviado" : "pendiente"} · Aviso a ti: {o.owner_email_sent ? "enviado" : "pendiente"}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tab === "carts" && (
          <div data-testid="admin-carts-view">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h1 className="font-display text-4xl text-[#2D3732]">Carritos abandonados</h1>
              <button
                data-testid="carts-refresh"
                onClick={() => loadOrders(adminKey)}
                className="flex items-center gap-2 border border-[#A3B18A] text-[#2D3732] hover:bg-[#EEF1E8] rounded-full px-4 py-2 text-sm transition-colors"
              >
                <ArrowClockwise size={15} className={ordersLoading ? "animate-spin" : ""} /> Actualizar
              </button>
            </div>
            <p className="text-[#4A554E] mt-3 font-light max-w-xl">
              Personas que llegaron a la pantalla de pago hace más de 30 minutos y no completaron la compra.
            </p>
            <div className="mt-8 space-y-4">
              {carts.length === 0 && !ordersLoading && (
                <p data-testid="carts-empty" className="text-[#6B705C] font-light bg-white border border-[#EBEBEB] rounded-2xl p-8 text-center">
                  No hay carritos abandonados. Cuando alguien deje productos sin pagar, aparecerán aquí con su detalle.
                </p>
              )}
              {carts.map((c, i) => (
                <div key={c.session_id} data-testid={`cart-card-${i}`} className="bg-white border border-[#EBEBEB] rounded-2xl p-6">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-[#2D3732] font-medium">
                        {c.buyer_email || "Email no facilitado"}
                      </p>
                      <p className="text-xs text-[#9CA3AF] mt-1">
                        {c.created_at ? new Date(c.created_at).toLocaleString("es-ES") : ""}
                      </p>
                    </div>
                    <span data-testid={`cart-total-${i}`} className="text-sm text-[#2D3732] font-medium">
                      {((c.amount || 0) / 100).toFixed(2)} €
                    </span>
                  </div>
                  <ul className="mt-4 space-y-1.5">
                    {(c.items || []).map((it, j) => (
                      <li key={j} data-testid={`cart-item-${i}-${j}`} className="text-sm text-[#4A554E] font-light flex items-start gap-2 flex-wrap">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#A3B18A] mt-1.5 flex-shrink-0" />
                        {describeItem(it)}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "images" && (
          <div data-testid="admin-images-view">
            <h1 className="font-display text-4xl text-[#2D3732]">Gestor de imágenes</h1>
            <p className="text-[#4A554E] mt-3 font-light max-w-xl">
              Sube tus propias fotos para cada sección. Formatos: JPG, PNG, WEBP (máx. 6 MB). Los cambios
              se ven al instante en la web.
            </p>

            <div className="mt-8 bg-white border border-[#EBEBEB] rounded-2xl p-6">
              <p className="text-[#2D3732] font-medium">Vídeo "Por dentro"</p>
              <p className="text-xs text-[#9CA3AF] mt-1">
                Pega un enlace de YouTube, Vimeo o un archivo .mp4 y se mostrará en la sección
                "Por dentro" de la web. Déjalo vacío para quitarlo.
              </p>
              <div className="flex flex-col sm:flex-row gap-2 mt-4">
                <input
                  data-testid="admin-video-input"
                  type="url"
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="flex-1 bg-[#FAF9F6] border border-[#EBEBEB] rounded-full px-5 py-3 text-sm text-[#2D3732] focus:outline-none focus:ring-2 focus:ring-[#A3B18A]"
                />
                <button
                  data-testid="admin-video-save"
                  onClick={saveVideo}
                  disabled={savingVideo}
                  className="bg-[#A3B18A] hover:bg-[#8A9A6F] text-white rounded-full px-6 py-3 text-sm transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {savingVideo ? <SpinnerGap size={16} className="animate-spin" /> : "Guardar"}
                </button>
              </div>
            </div>

            <div className="mt-10 space-y-4">
              {slots.map((slot) => {
                const isUp = uploaded.includes(slot.key);
                return (
                  <div
                    key={slot.key}
                    data-testid={`slot-${slot.key}`}
                    className="flex items-center gap-5 bg-white border border-[#EBEBEB] rounded-2xl p-4"
                  >
                    <div className="w-20 h-20 rounded-xl overflow-hidden bg-[#F6F1E7] flex-shrink-0 flex items-center justify-center">
                      {isUp ? (
                        <img
                          src={`${API}/images/${slot.key}?t=${Date.now()}`}
                          alt={slot.label}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <UploadSimple size={22} className="text-[#A3B18A]" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[#2D3732] font-medium truncate">{slot.label}</p>
                      <p className="text-xs mt-0.5 flex items-center gap-1">
                        {isUp ? (
                          <span className="text-[#8A9A6F] flex items-center gap-1">
                            <CheckCircle size={14} weight="fill" /> Imagen personalizada
                          </span>
                        ) : (
                          <span className="text-[#9CA3AF]">Usando imagen por defecto</span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <label
                        className="cursor-pointer bg-[#A3B18A] hover:bg-[#8A9A6F] text-white rounded-full px-4 py-2.5 text-xs transition-colors flex items-center gap-2"
                        data-testid={`upload-${slot.key}`}
                      >
                        {busy === slot.key ? (
                          <SpinnerGap size={14} className="animate-spin" />
                        ) : (
                          <UploadSimple size={14} />
                        )}
                        Subir
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFile(slot.key, e.target.files[0])}
                        />
                      </label>
                      {isUp && (
                        <button
                          onClick={() => removeImage(slot.key)}
                          data-testid={`delete-${slot.key}`}
                          className="text-[#9CA3AF] hover:text-[#b05656] transition-colors p-2"
                        >
                          <Trash size={18} weight="light" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
